import React, { useState } from 'react';
import CoreSkeletonBox from 'src/shared/CoreSkeletonBox';
import styled from 'styled-components';
import {
	firstSectionWrapperClass,
	updatedTextClass,
	updatedTextWrapperClass,
} from '@components/report/style.css';
import { useIgReportGet } from 'src/apis/orval/endpoints/v2-report/v2-report';
import { useSearchParams } from 'react-router-dom';
import { KeyMetricsIgAdapter } from '@components/report/section-content/ig/key-metrics/KeyMetricsIgAdapter';
import { FollowerGrowthIgAdapter } from '@components/report/section-content/ig/follower-growth/FollowerGrowthAdapter';
import { AudienceAdapter } from '@components/report/section-content/common/audience/AudienceAdapter';
import { EngagementIgAdapter } from '@components/report/section-content/ig/engagement/EngagementIgAdapter';
import { ContentIgAdapter } from '@components/report/section-content/ig/content/ContentIgAdapter';
import { FeedContentAdapter } from '@components/report/section-content/ig/feed-content/FeedContentAdapter';
import { VideoContentIgAdapter } from '@components/report/section-content/ig/video-content/VideoContentIgAdapter';
import { PopularContentAdapter } from '@components/report/section-content/ig/popular-content/PopularContentAdapter';
import { RecommendationAdapter } from '@components/report/section-content/ig/recommendations/RecommendationAdapter';
import { SimilarGroupIgAdapter } from '@components/report/section-content/ig/similar-group/SimilarGroupAdapter';
import { useReportPollingContext } from '@components/report/report-layout/ReportPollingContext';
import { CoreButton, CoreSelect } from '@featuring-corp/components';
import { Trans, useTranslation } from 'react-i18next';
import KeyMetricsIg from '@components/report/section-content/ig/key-metrics/KeyMetrics';
import ReportSection from '@components/report/report-section/ReportSection';
import FollowerGrowthIg from '@components/report/section-content/ig/follower-growth/FollowerGrowth';
import Audience from '@components/report/section-content/common/audience/Audience';
import EngagementIg from '@components/report/section-content/ig/engagement/EngagementIg';
import ContentIg from '@components/report/section-content/ig/content/ContentIg';
import FeedContent from '@components/report/section-content/ig/feed-content/FeedContent';
import ReportSubSection from '@components/report/report-sub-section/ReportSubSection';
import VideoContentIg from '@components/report/section-content/ig/video-content/VideoContentIg';
import PopularContent from '@components/report/section-content/ig/popular-content/PopularContent';
import Recommendations from '@components/report/section-content/ig/recommendations/Recommendations';
import SimilarGroupIg, {
	SimilarGroupProps,
} from '@components/report/section-content/ig/similar-group/SimilarGroupIg';
import ReportIndex from '../../report-index/ReportIndex';
import useScrollIntoView from '../../useScrollntoView';
import SectionMessages from '../../section-messages/SectionMessages';

const indexItems = [
	'핵심 지표',
	'팔로워 성장률',
	'오디언스',
	'인게이지먼트',
	'콘텐츠',
	'맞춤 추천 계정',
	'유사 그룹',
];

const InstagramContainer = () => {
	const { t } = useTranslation('infReportInstagram');
	const [search_params] = useSearchParams();
	const platformPk = search_params.get('pk') as string;
	const {
		isReportLoading,
		isUpdateLoading,
		needUpdate,
		readyForUpdate,
		isPollingFailed,
	} = useReportPollingContext();

	const { selectedSection, moveScroll } = useScrollIntoView('h2');

	const [sortFeedContentBy, setSortFeedContentBy] = useState('newest');
	const [sortVideoContentBy, setSortVideoContentBy] = useState('newest');
	const [sortPopularContentBy, setSortPopularContentBy] = useState('newest');

	const {
		data: {
			keyMetrics,
			followerGrowth,
			audience,
			engagement,
			content,
			feedContent,
			videoContent,
			popularContent,
			recommendation,
			similarGroup,
		} = {},
		isLoading: isQueryLoading,
		isError: isQueryError,
		error,
		refetch,
	} = useIgReportGet(platformPk, {
		query: {
			select: (data) => ({
				keyMetrics: new KeyMetricsIgAdapter(data).adapt(),
				followerGrowth: new FollowerGrowthIgAdapter(data).adapt(),
				audience: new AudienceAdapter(data).adapt(),
				engagement: new EngagementIgAdapter(data).adapt(),
				content: new ContentIgAdapter(data).adapt(),
				feedContent: new FeedContentAdapter(data).adapt(
					sortFeedContentBy as 'newest' | 'engagement',
				),
				videoContent: new VideoContentIgAdapter(data).adapt(
					sortVideoContentBy as 'newest' | 'views',
				),
				popularContent: new PopularContentAdapter(data).adapt(
					sortPopularContentBy as 'newest' | 'engagement',
				),
				recommendation: new RecommendationAdapter(data).adapt(),
				similarGroup: new SimilarGroupIgAdapter(data).adapt(),
			}),
			refetchOnWindowFocus: false,
			enabled: !!platformPk,
			retry: false,
		},
	});

	const isLoading = isQueryLoading || isReportLoading;
	// polling 이 완료 없이 종료된 경우(에러/시간 초과)도 에러 화면과 동일하게 처리
	const isError = isQueryError || isPollingFailed;

	return (
		<>
			<ReportIndex
				indexItems={indexItems}
				selectedSection={selectedSection ?? ''}
				handleMoveScroll={moveScroll}
			/>
			<ReportContentWrapper>
				{/* 섹션 메시지 */}
				<SectionMessages
					isError={isError}
					errorCode={(error as any)?.statusCode}
					isLoading={isLoading}
				/>

				<div className={firstSectionWrapperClass}>
					<div className={updatedTextWrapperClass}>
						<p className={updatedTextClass}>
							Last update:{' '}
							{isLoading ? (
								<CoreSkeletonBox _width="80px" _height="14px" radius="4px" />
							) : (
								keyMetrics?.updatedAt ?? ' - '
							)}
						</p>
						{needUpdate && (
							<CoreButton
								text={t('report.instagram.ig-report.button.need-update')}
								buttonType="secondary"
								size="sm"
								isLoading={isUpdateLoading}
								disabled={!isUpdateLoading && !readyForUpdate}
								onClick={() => refetch()}
							/>
						)}
					</div>

					{/* 핵심 지표 */}
					<ReportSection
						title={t('report.instagram.ig-report.item.key-metrics')}
						tooltip={t('report.instagram.ig-report.report-section.key-metrics.tooltip')}
						description={t(
							'report.instagram.ig-report.report-section.key-metrics.description',
						)}
					>
						<KeyMetricsIg
							isLoading={isLoading}
							isError={isError}
							{...keyMetrics?.props}
						/>
					</ReportSection>
				</div>

				{/* 팔로워 성장률 */}
				<ReportSection
					title={t('report.instagram.ig-report.item.follower-growth')}
					description={t(
						'report.instagram.ig-report.report-section.follower-growth.description',
					)}
				>
					<FollowerGrowthIg isLoading={isLoading} isError={isError} {...followerGrowth} />
				</ReportSection>

				{/* 오디언스 */}
				<ReportSection
					title={t('report.instagram.ig-report.item.audience')}
					description={t(
						'report.instagram.ig-report.report-section.audience.description',
					)}
				>
					<Audience {...audience} isLoading={isLoading} isError={isError} />
				</ReportSection>

				<ReportSection
					title={t('report.instagram.ig-report.item.engagement')}
					description={t(
						'report.instagram.ig-report.report-section.engagement.description',
					)}
				>
					<EngagementIg isLoading={isLoading} isError={isError} {...engagement} />
				</ReportSection>

				{/* 콘텐츠 */}
				<ReportSection title={t('report.instagram.ig-report.item.content')}>
					<ContentIg {...content} isLoading={isLoading} isError={content?.isError} />
				</ReportSection>

				{/* 피드 콘텐츠 */}
				<ReportSubSection
					title={t('report.instagram.ig-report.report-subsection.feed-content.title')}
					tooltip={t(
						'report.instagram.ig-report.report-subsection.feed-content.title-content.tooltip',
					)}
					titleContent={
						<CoreSelect
							size="sm"
							width={171}
							defaultValue={sortFeedContentBy}
							setValue={setSortFeedContentBy}
							dropdownZIndex={10000}
						>
							<CoreSelect.Item value="newest">{t('sort-content.newest')}</CoreSelect.Item>
							<CoreSelect.Item value="engagement">
								{t('sort-content.engagement')}
							</CoreSelect.Item>
						</CoreSelect>
					}
				>
					<FeedContent {...feedContent} isLoading={isLoading} isError={isError} />
				</ReportSubSection>

				{/* 비디오 콘텐츠 */}
				<ReportSubSection
					title={t('report.instagram.ig-report.report-subsection.video-content.title')}
					tooltip={t(
						'report.instagram.ig-report.report-subsection.video-content.title-content.tooltip',
					)}
					titleContent={
						<CoreSelect
							size="sm"
							width={171}
							defaultValue={sortVideoContentBy}
							setValue={setSortVideoContentBy}
							dropdownZIndex={10000}
						>
							<CoreSelect.Item value="newest">{t('sort-content.newest')}</CoreSelect.Item>
							<CoreSelect.Item value="views">{t('sort-content.views')}</CoreSelect.Item>
						</CoreSelect>
					}
				>
					<VideoContentIg {...videoContent} isLoading={isLoading} isError={isError} />
				</ReportSubSection>

				{/* 인기 콘텐츠 - 없을 경우 노출하지 않음 */}
				{!(popularContent?.items && popularContent?.items?.length === 0) && (
					<ReportSubSection
						title={t(
							'report.instagram.ig-report.report-subsection.popular-content.title',
						)}
						tooltip={t(
							'report.instagram.ig-report.report-subsection.popular-content.title-content.tooltip',
						)}
						titleContent={
							<CoreSelect
								size="sm"
								width={171}
								defaultValue={sortPopularContentBy}
								setValue={setSortPopularContentBy}
								dropdownZIndex={10000}
							>
								<CoreSelect.Item value="newest">
									{t('sort-content.newest')}
								</CoreSelect.Item>
								<CoreSelect.Item value="engagement">
									{t('sort-content.engagement')}
								</CoreSelect.Item>
							</CoreSelect>
						}
					>
						<PopularContent {...popularContent} isLoading={isLoading} isError={isError} />
					</ReportSubSection>
				)}

				{/* 맞춤 추천 계정 */}
				<ReportSection
					title={t('report.instagram.ig-report.item.recommendations')}
					tooltip={t('report.instagram.ig-report.report-section.recommendations.tooltip')}
					description={t(
						'report.instagram.ig-report.report-section.recommendations.description',
					)}
				>
					<Recommendations {...recommendation} isLoading={isLoading} isError={isError} />
				</ReportSection>

				{/* 유사 그룹 */}
				<ReportSection
					title={t('report.instagram.ig-report.item.similar-group')}
					tooltip={t('report.instagram.ig-report.report-section.similar-group.tooltip')}
					description={
						<>
							<Trans
								t={t}
								i18nKey="report.instagram.ig-report.report-section.similar-group.description"
								components={[
									<span key={'user-name'}>{similarGroup?.desc?.userName ?? '-'}</span>,
									<span key={'category'}>{similarGroup?.desc?.category ?? '-'}</span>,
									<span key={'followers'}>
										{t('similar-group.desc.followers', {
											followers: similarGroup?.desc?.followers ?? '-',
										})}
									</span>,
								]}
							/>
						</>
					}
				>
					<SimilarGroupIg
						{...(similarGroup?.props as SimilarGroupProps<string[]>)}
						isLoading={isLoading}
						isError={isError}
					/>
				</ReportSection>
			</ReportContentWrapper>
		</>
	);
};

const ReportContentWrapper = styled.div`
	overflow: hidden;
	height: auto;
	min-height: 100%;
	flex-grow: 1;
	flex-shrink: 0;
	padding: 60px 0 150px;

	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;

	gap: 80px;
`;

export default InstagramContainer;
